import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import AgeGroupChip from 'components/age-group-chip'
import useDetailStyles from '../detail-styles'
import DownloadPlanHeaderAction from './download-plan-header-action'
import { downloadLessonPlan } from '../lib/plan-download'
import useRegisterActivityPlanDetailHeader from '../hooks/use-register-activity-plan-detail-header'
import ActivityDetailContent from './activity-detail-content'

const getActivityTitle = activity =>
  activity?.title || activity?.details?.shortTitle

const getActivitySubtitle = activity => {
  const setupDuration = activity?.details?.setupDuration
  const durationMins = activity?.details?.durationMins

  return [
    setupDuration != null ? `Setup ${setupDuration} min` : null,
    durationMins != null ? `Duration ${durationMins} min` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

const resolveActivityRef = (activityRef, activityHitsById) => {
  if (!activityRef) {
    return null
  }

  if (typeof activityRef === 'object') {
    const activityId = activityRef.objectID || activityRef.id

    return (activityId && activityHitsById[activityId]) || activityRef
  }

  return activityHitsById[activityRef] || null
}

const getActivityKey = activityRef => {
  if (typeof activityRef === 'object') {
    return activityRef.objectID || activityRef.id || activityRef.title
  }

  return activityRef
}

const LessonPlanDetailView = ({
  planHit,
  activityHits,
  inlineActions = false,
}) => {
  const classes = useDetailStyles()
  const activityHitsById = activityHits.reduce((map, hit) => {
    map[hit.objectID] = hit
    return map
  }, {})

  const subtitle =
    planHit.totalDurationMins != null
      ? `${planHit.totalDurationMins} minutes total`
      : ''

  const {
    HeaderActionsPortal,
    renderActionsInline,
  } = useRegisterActivityPlanDetailHeader({
    title: planHit.title,
    subtitle,
    inlineActions,
  })

  const downloadAction = (
    <DownloadPlanHeaderAction
      buttonLabel="Download Plan"
      dialogTitle="Download plan"
      dialogDescription="Download this lesson plan as a text file. Click Download to save this lesson plan to your device."
      onDownload={() => downloadLessonPlan(planHit, activityHits)}
    />
  )

  return (
    <>
      {!renderActionsInline ? (
        <HeaderActionsPortal>{downloadAction}</HeaderActionsPortal>
      ) : null}
      <Box className={classes.contentContainer}>
        {renderActionsInline ? (
          <Box className={classes.header}>
            <Box className={classes.headerCopy}>
              <Typography component="h1" className={classes.title}>
                {planHit.title}
              </Typography>
              {subtitle ? (
                <Typography component="p" className={classes.meta}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {downloadAction}
          </Box>
        ) : null}
        {(planHit.sections || []).map(section => {
          const sectionActivities = (section.activities || [])
            .map(activityRef =>
              resolveActivityRef(activityRef, activityHitsById),
            )
            .filter(Boolean)

          if (sectionActivities.length === 0) {
            return null
          }

          return (
            <Box key={section.name} className={classes.planSection}>
              {sectionActivities.map(activity => {
                const activityTitle = getActivityTitle(activity)
                const activitySubtitle = getActivitySubtitle(activity)
                const heading = activityTitle || section.name
                const showSectionLabel =
                  section.name && section.name !== heading

                return (
                  <Box
                    key={getActivityKey(activity)}
                    className={classes.planActivityGroup}
                  >
                    <Box className={classes.activityHeader}>
                      {showSectionLabel ? (
                        <AgeGroupChip label={section.name} />
                      ) : null}
                      <Box className={classes.activityTitleGroup}>
                        <Typography
                          component="h2"
                          className={classes.activityHeading}
                        >
                          {heading}
                        </Typography>
                        {activitySubtitle ? (
                          <Typography component="p" className={classes.meta}>
                            {activitySubtitle}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                    <ActivityDetailContent hit={activity} />
                  </Box>
                )
              })}
            </Box>
          )
        })}
      </Box>
    </>
  )
}

LessonPlanDetailView.propTypes = {
  planHit: PropTypes.object.isRequired,
  activityHits: PropTypes.arrayOf(PropTypes.object).isRequired,
  inlineActions: PropTypes.bool,
}

export default LessonPlanDetailView
